import React from "react";
import { toast } from "sonner";
import { Facebook, Twitter, Copy } from "lucide-react";

// Build a single, ready-to-share caption from an abuser record.
// Public-facing wording: factual, neutral, sourced. No editorializing.
function buildCaption(abuser) {
  const statusTag =
    abuser.status === "Convicted"
      ? "CONVICTED"
      : abuser.status?.toUpperCase().includes("PENDING") || abuser.status?.toUpperCase().includes("TRIAL")
      ? "ON TRIAL — PENDING"
      : abuser.status?.toUpperCase() || "REGISTRY RECORD";

  const where = [abuser.city, abuser.state].filter(Boolean).join(", ");
  const animals = abuser.animals_affected ? ` Animals affected: ${abuser.animals_affected}.` : "";
  const sentence = abuser.sentence ? ` Sentence: ${abuser.sentence}` : "";
  // Trim summary to keep within social-post limits.
  const summary = (abuser.crime_summary || "").trim();
  const trimmed = summary.length > 220 ? summary.slice(0, 217) + "…" : summary;

  return (
    `[${statusTag}] ${abuser.name} — ${abuser.crime_type} (${where}).\n` +
    `${trimmed}${animals}${sentence}\n\n` +
    `Verify the source + full record on Paws on Record:\n` +
    `#PawsOnRecord #AnimalCruelty #${(abuser.state_code || "US").toUpperCase()} #ProtectAnimals`
  );
}

export default function ShareCaseButtons({ abuser, variant = "dark" }) {
  if (!abuser) return null;
  const url = typeof window !== "undefined" ? window.location.href : "";
  const caption = buildCaption(abuser);

  const onFacebook = () => {
    const u = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}&quote=${encodeURIComponent(caption)}`;
    window.open(u, "_blank", "noopener,noreferrer,width=640,height=640");
  };
  const onTwitter = () => {
    // X/Twitter: cap text generously; URL appended automatically.
    const text = caption.length > 260 ? caption.slice(0, 257) + "…" : caption;
    const u = `https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(url)}`;
    window.open(u, "_blank", "noopener,noreferrer,width=640,height=640");
  };
  const onCopy = async () => {
    try {
      await navigator.clipboard.writeText(`${caption}\n${url}`);
      toast.success("Caption + link copied. Paste anywhere.");
    } catch {
      toast.error("Couldn't copy — try selecting the page URL manually.");
    }
  };

  const baseBtn =
    variant === "dark"
      ? "inline-flex items-center gap-1.5 px-3 py-1.5 text-[11px] font-bold uppercase tracking-widest border border-white text-white hover:bg-white hover:text-zinc-900 transition-colors"
      : "inline-flex items-center gap-1.5 px-3 py-1.5 text-[11px] font-bold uppercase tracking-widest border border-zinc-900 text-zinc-900 hover:bg-zinc-900 hover:text-white transition-colors";

  return (
    <div className="flex flex-wrap items-center gap-2" data-testid="share-case-buttons">
      <button
        type="button"
        onClick={onFacebook}
        className={baseBtn}
        title="Share this case on Facebook"
        data-testid="share-fb"
      >
        <Facebook size={13} /> Facebook
      </button>
      <button
        type="button"
        onClick={onTwitter}
        className={baseBtn}
        title="Share this case on X (Twitter)"
        data-testid="share-x"
      >
        <Twitter size={13} /> X
      </button>
      <button
        type="button"
        onClick={onCopy}
        className={baseBtn}
        title="Copy caption + link to clipboard"
        data-testid="share-copy"
      >
        <Copy size={13} /> Copy
      </button>
    </div>
  );
}
